import React, { useEffect, useState } from 'react'
import { IconButton, CircularProgress } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'
import { LineChart, Text } from '@tremor/react'

import moment, { Moment } from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import {
  getTicketScanStats,
  TicketStats,
  TicketScanStatsResponse,
  TicketScanStatsParams,
} from './api'

const downloadCSV = (data: TicketStats[]) => {
  const csvRows = [
    ['Mes', 'Tickets Físicos', 'Escaneos en App', 'Porcentaje'],
    ...data.map(row => [
      moment(`${row.year}-${row.month}-01`).format('MMMM'),
      row.physicalTickets,
      row.appScans,
      `${row.percentage}%`,
    ]),
  ]
  const csvContent = csvRows.map(e => e.join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = 'tickets_por_app.csv'
  a.click()
  URL.revokeObjectURL(url)
}

interface TicketsByAppGeneralProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period1: { startDate: Moment; endDate: Moment }
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period2: { startDate: Moment; endDate: Moment }
  compara: boolean
  trigger: number
  storeIds?: number[]
  onLoaded?: () => void
}

export const TicketsByAppGeneral = ({
  period1,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  period2,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  compara,
  trigger,
  storeIds,
  onLoaded,
}: TicketsByAppGeneralProps) => {
  const [data, setData] = useState<TicketStats[]>([])
  const [statsCall, statsLoading] = useApiCall<TicketScanStatsParams, TicketScanStatsResponse>(
    getTicketScanStats,
  )

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'TicketsByAppGeneral' })

  const fetchData = async () => {
    try {
      // Periodo 1 - usar fechas completas
      const startDate1 = period1.startDate
        .clone()
        .subtract(11, 'months')
        .startOf('month')
      const endDate1 = period1.endDate.clone()

      console.log('Fetching data for period 1:', {
        startDate: startDate1.format('YYYY-MM-DD'),
        endDate: endDate1.format('YYYY-MM-DD'),
      })

      const response1 = await statsCall({
        startDate: startDate1.format('YYYY-MM-DD'),
        endDate: endDate1.format('YYYY-MM-DD'),
        ...(storeIds && storeIds.length > 0 && { storeIds: storeIds.join(',') }),
      })

      console.log('Response 1:', response1)
      if (response1?.data) {
        setData(response1.data)
      }
    } catch (error) {
      console.error('Error fetching ticket statistics:', error)
    } finally {
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const latestData = data[data.length - 1]

  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ]

  const chartData = data
    .map(d => ({
      ...d,
      monthLabel: months[d.month - 1],
      Porcentaje: d.percentage,
      sortKey: moment(`${d.year}-${d.month}-01`).valueOf(),
    }))
    .sort((a, b) => a.sortKey - b.sortKey)

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-row justify-between items-start mb-2">
        <div className="flex flex-col gap-2">
          <Text className="font-bold text-base block mb-4">Tickets x APP</Text>
          {!statsLoading && (
            <Text className="text-4xl font-extrabold">
              {latestData ? `${latestData.percentage.toFixed(2)}%` : '--'}
            </Text>
          )}
        </div>
        <div className="flex flex-col items-end gap-4">
          <IconButton
            disabled={statsLoading}
            className="ml-2"
            aria-label="download"
            onClick={() => downloadCSV(data)}
          >
            <GetAppIcon />
          </IconButton>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <div style={{ minWidth: 1200 }}>
          {statsLoading ? (
            <div className="flex items-center justify-center py-8">
              <CircularProgress size={40} />
            </div>
          ) : (
            <LineChart
              className="h-80 w-full"
              data={chartData}
              index="monthLabel"
              categories={['Porcentaje']}
              colors={['teal']}
              valueFormatter={number => (number !== null ? `${number}%` : '')}
              showLegend={false}
              showGridLines
              showAnimation
              yAxisWidth={60}
              minValue={0}
              noDataText="No hay datos disponibles para mostrar en este momento."
            />
          )}
        </div>
      </div>
    </div>
  )
}

TicketsByAppGeneral.defaultProps = {
  storeIds: undefined,
  onLoaded: undefined,
}
